import { useTelemetry } from '@ctw/shared/context/telemetry/telemetry-boundary';
import { type ClassName, twx } from '@ctw/shared/utils/tailwind';
import { uniqueId } from 'lodash-es';
import { type PropsWithChildren, type ReactNode, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import { type PlacesType, Tooltip as ReactTooltip } from 'react-tooltip';

export interface TooltipProps {
  id: string;
  className?: ClassName;
  tooltipClassName?: ClassName;
  contentClassName?: ClassName;
  children: ReactNode;
  place?: PlacesType;
  hidden?: boolean;
  content: ReactNode;
  noArrow?: boolean;
  enabled?: boolean;
}

export function Tooltip({
  id,
  className,
  hidden,
  place = 'bottom',
  tooltipClassName,
  contentClassName,
  children,
  content,
  noArrow = false,
  enabled = true,
}: TooltipProps) {
  const telemetry = useTelemetry();
  const instanceId = useMemo(() => `tooltip-${id}-instance${uniqueId()}`, [id]);
  const timer = useRef<ReturnType<typeof setTimeout>>(undefined);

  return (
    <>
      <div
        data-tooltip-id={instanceId}
        className={twx(className, { 'pointer-events-none': !enabled })}
      >
        {children}
      </div>
      <TooltipPortal>
        <ReactTooltip
          positionStrategy="fixed"
          place={place}
          noArrow={noArrow}
          id={instanceId}
          isOpen={enabled ? undefined : false}
          render={() => <span className={twx(contentClassName)}>{content}</span>}
          className={twx('z-tooltip', { hidden }, tooltipClassName)}
          style={{
            padding: '0.3rem 0.5rem 0.3rem 0.5rem',
          }}
          afterShow={() => {
            timer.current = setTimeout(() => {
              timer.current = undefined;
              telemetry.trackInteraction('tooltip_show', {
                tooltipId: id,
              });
            }, 1000);
          }}
          afterHide={() => {
            clearTimeout(timer.current);
            if (timer.current === undefined) {
              telemetry.trackInteraction('tooltip_hide', {
                tooltipId: id,
              });
            }
          }}
        />
      </TooltipPortal>
    </>
  );
}
const TooltipPortal = ({ children }: PropsWithChildren) => {
  return createPortal(children, document.body);
};
